import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, of, switchMap, tap } from 'rxjs';
import { CommonModule } from '@angular/common';

import { SearchIcon } from '../../../../shared/ui/icons/search-icon/search-icon';
import { AutoCompleteConfirmIcon } from '../../../../shared/ui/icons/auto-complete-confirm-icon/auto-complete-confirm-icon';
import { LocationIcon } from '../../../../shared/ui/icons/location-icon/location-icon';
import { HumanIcon } from '../../../../shared/ui/icons/human-icon/human-icon';
import { IdHashIcon } from '../../../../shared/ui/icons/id-hash-icon/id-hash-icon';
import { PharmacySearchItem } from '../../../../pages/admin-dashboard/account-statement/account-statement-auto-complete/account-statement-auto-complete.model';
import { AccountAutoComplete } from '../../../../services/Account-Statement/account-auto-complete';

@Component({
  selector: 'app-account-statement-auto-complete-search',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    SearchIcon,
    AutoCompleteConfirmIcon,
    LocationIcon,
    HumanIcon,
    IdHashIcon,
  ],
  templateUrl: './account-statement-auto-complete-search.html',
  styleUrl: './account-statement-auto-complete-search.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountStatementAutoCompleteSearch implements OnInit {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private eRef = inject(ElementRef);

  private accountStatementService = inject(AccountAutoComplete);

  searchControl = new FormControl<string>('', { nonNullable: true });

  pharmaciesList = signal<PharmacySearchItem[]>([]);
  isLoading = signal<boolean>(false);
  showDropdown = signal<boolean>(false);
  selectedPharmacy = signal<PharmacySearchItem | null>(null);
  isNextLoading = signal<boolean>(false);

  ngOnInit(): void {
    this.setupSearch();
  }

  setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap((term) => {
          if (this.selectedPharmacy()) {
            this.selectedPharmacy.set(null);
          }

          const value = (term ?? '').trim();

          if (!value || value.length < 2) {
            this.showDropdown.set(false);
            this.pharmaciesList.set([]);
            this.isLoading.set(false);
            return;
          }

          this.isLoading.set(true);
          this.showDropdown.set(true);
        }),
        switchMap((term) => {
          const value = (term ?? '').trim();

          if (!value || value.length < 2) {
            return of([]);
          }

          return this.accountStatementService
            .getAutoCompletePharmacies({
              quickSearch: value,
              pageNum: 0,
              pageSize: 25,
            })
            .pipe(
              map((res) => {
                const backendList = res.list || [];
                return backendList.map(
                  (item) =>
                    ({
                      id: item.id,
                      pharmacyId: item.id.toString(),
                      ownerName: item.pharmacyOwner?.fullName || '---',
                      pharmacyName: item.nickName || '---',
                      location: item.address || '---',
                    }) as PharmacySearchItem,
                );
              }),
              catchError((error) => {
                console.error('Error fetching pharmacies:', error);
                return of([]);
              }),
              finalize(() => this.isLoading.set(false)),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((mappedList) => {
        this.pharmaciesList.set(mappedList);
      });
  }

  selectPharmacy(pharmacy: PharmacySearchItem): void {
    this.selectedPharmacy.set(pharmacy);
    this.showDropdown.set(false);
  }

  onSearchFocus(): void {
    const value = (this.searchControl.value ?? '').trim();
    if (value.length >= 2 && !this.selectedPharmacy() && this.pharmaciesList().length > 0) {
      this.showDropdown.set(true);
    }
  }

  goToStatement(): void {
    const selected = this.selectedPharmacy();
    if (!selected || this.isNextLoading()) return;

    this.isNextLoading.set(true);

    this.router
      .navigate(['admin/account-statement/view-account-statement', selected.id], {
        state: { pharmacy: selected },
      })
      .then(() => {
        this.isNextLoading.set(false);
      })
      .catch(() => {
        this.isNextLoading.set(false);
      });
  }

  cancel(): void {
    this.router.navigate(['/invoices']);
  }

  @HostListener('document:click', ['$event'])
  clickout(event: MouseEvent): void {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.showDropdown.set(false);
    }
  }
}
